/**
 * Helpers for reading the machine-info tree: walking a component's locations and bearings,
 * finding a component node, and formatting timestamps for the activity surfaces.
 */

/**
 * Walk a single component's locations and bearings (not its sub-components).
 * Exported for use in Digital3DView where a full-tree traversal is needed.
 */
export function walkInfoLocationsForComponent(infoComponent, visit) {
  if (!infoComponent) return;
  (infoComponent.locations || []).forEach(visit);
  const br = infoComponent.bearings;
  if (Array.isArray(br)) {
    br.forEach(visit);
  } else if (br && typeof br === 'object') {
    Object.values(br).forEach(visit);
  }
}

/**
 * Walk locations of ALL components in a nested tree, including sub-components.
 * Used when a full-tree scan is needed (e.g. building a location→sensor map).
 */
export function walkAllComponentLocations(components, visit) {
  if (!Array.isArray(components)) return;
  components.forEach((node) => {
    walkInfoLocationsForComponent(node, visit);
    walkAllComponentLocations(node.components, visit);
  });
}

/**
 * Map of location id -> the first sensor mounted at it, across the whole
 * machine-info tree. The scene uses this to decide which slot renders a sensor.
 *
 * `dt_slot_index` is a SESSION-LOCAL hint, not storage. Nothing persists it: the API never
 * sends it, and `Digital3DView.patchAssignedSensorSlotIndex` writes it into this store after a
 * successful assignment purely so the scene keeps the sensor on the exact pad the user dropped
 * on for the rest of the session. Normalising it onto `slotIndex` is what makes that work.
 *
 * Across a reload the station is recovered from `orientation` instead — the mount code IS the
 * position in this product's vocabulary (TAR around the bearing, TRA on the side plate), which
 * is also how the 2D graphic picks its mote element. So an absent `dt_slot_index` is normal and
 * means "ask orientation", never "slot 0".
 */
export function collectMountedSensorsByLocation(infoDetails) {
  const mountedSensors = {};
  if (!infoDetails || !infoDetails.components) return mountedSensors;
  walkAllComponentLocations(infoDetails.components, (location) => {
    const firstMountedSensor = (location.motes && location.motes[0])
      || (location.tx_sensors && location.tx_sensors[0])
      || null;
    if (location.id != null && firstMountedSensor) {
      const slotIndex = firstMountedSensor.dt_slot_index != null
        ? firstMountedSensor.dt_slot_index
        : firstMountedSensor.slotIndex;
      mountedSensors[String(location.id)] = slotIndex != null
        ? { ...firstMountedSensor, slotIndex }
        : firstMountedSensor;
    }
  });
  return mountedSensors;
}

/**
 * Find a component node by id in a nested machine-info component tree.
 * Returns the matched node directly, or null.
 */
export function findInfoComponent(infoDetails, componentId) {
  if (!infoDetails || componentId == null) return null;
  const stack = [].concat(infoDetails.components || []);
  while (stack.length) {
    const node = stack.pop();
    if (node) {
      if (String(node.id) === String(componentId)) return node;
      if (node.components && node.components.length) stack.push(...node.components);
    }
  }
  return null;
}

/**
 * Size of the sibling set `componentId` belongs to, counting itself. Top-level components are
 * siblings of one another, so the machine node counts as their parent.
 *
 * The delete endpoint refuses to remove the last child of a parent, and the last top-level
 * component of a machine (component_controller.delete_component). Both reduce to a sibling set
 * of one, so this is what the view checks to gate the control rather than let the user confirm
 * a removal the backend was always going to reject.
 *
 * Returns 0 when the component is not in the tree, which callers must not read as "no siblings".
 */
export function countInfoSiblings(infoDetails, componentId) {
  if (!infoDetails || componentId == null) return 0;
  const stack = [infoDetails];
  while (stack.length) {
    const node = stack.pop();
    const children = (node && node.components) || [];
    const isSibling = children.some(c => c && String(c.id) === String(componentId));
    if (isSibling) return children.length;
    children.forEach((c) => { if (c) stack.push(c); });
  }
  return 0;
}


/**
 * Normalises a timestamp to epoch milliseconds.
 *
 * A numeric timestamp is ambiguous: this module treated every number as epoch
 * *seconds* while eventHighlightEntries.js handed the same field to `new Date(n)`,
 * which reads epoch *milliseconds*. Whichever was wrong for a given payload
 * failed silently and in the dangerous direction — a millisecond value scaled by
 * 1000 lands ~50,000 years in the future, so the age comes out negative and
 * every reading reports as fresh no matter how old it is.
 *
 * The two encodings are a factor of 1000 apart, so magnitude disambiguates them
 * unambiguously for any realistic date: 1e11 is 1973 in milliseconds and the
 * year 5138 in seconds, so anything below it can only be seconds.
 */
const SECONDS_MS_THRESHOLD = 1e11;

export function timestampToMillis(timestamp) {
  if (timestamp == null || timestamp === '') return NaN;
  if (typeof timestamp === 'number') {
    if (!Number.isFinite(timestamp)) return NaN;
    return timestamp < SECONDS_MS_THRESHOLD ? timestamp * 1000 : timestamp;
  }
  return new Date(timestamp).getTime();
}


/**
 * Relative time string e.g. "2 min ago". Returns "just now" for < 5 s, and an absolute
 * date-time once the event is more than a day old.
 *
 * `t` is optional so the pure-data callers and tests can stay unchanged, but every UI caller
 * must pass it: without it this function is the one place a localised panel still renders
 * English, and the multi-day branch formats to the browser locale rather than the app's.
 */
export function relativeTime(timestamp, t, language) {
  if (!timestamp) return '—';
  const translate = (key, defaultValue, options) =>
    (t ? t(`common.relativeTime.${key}`, { defaultValue, ...options }) : defaultValue);
  try {
    const ms = timestampToMillis(timestamp);
    if (Number.isNaN(ms)) return '—';
    const diff = Date.now() - ms;
    const sec = Math.round(diff / 1000);
    if (sec < 5) return translate('justNow', 'just now');
    if (sec < 60) return translate('secondsAgo', `${sec}s ago`, { count: sec });
    const min = Math.round(sec / 60);
    if (min < 60) return translate('minutesAgo', `${min} min ago`, { count: min });
    const hr = Math.round(min / 60);
    if (hr < 24) return translate('hoursAgo', `${hr} hr ago`, { count: hr });
    return new Date(ms).toLocaleString(language || undefined);
  } catch {
    return '—';
  }
}
